import { SITE } from "@/lib/site";
import { PageImageGallery } from "@/components/page-image-gallery";
import { ogImageMeta } from "@/lib/og";
import { Link, createFileRoute } from "@tanstack/react-router";

import { Badge } from "@/components/ui/badge";
import { ProseSection } from "@/components/prose-section";
import { MethodLogo } from "@/components/method-logo";
import { CoverageInfographic, DepositSpeedInfographic, PayoutSpeedInfographic, SpreadInfographic } from "@/components/infographics";
import { Card, CardContent } from "@/components/ui/card";
import { formatEuro } from "@/lib/format";
import { getPublishedMethods } from "@/lib/queries";
import { ArrowRight } from "lucide-react";

export const Route = createFileRoute("/betaalmethoden/")({
  head: () => ({
    meta: [
      { title: "Alle Casino Betaalmethoden 2026: 17 Opties Naast Elkaar" },
      {
        name: "description",
        content:
          "Vind de beste casino betaalmethode voor november 2026. iDEAL, Trustly, PayPal, Skrill en meer getest op snelheid, limieten en kosten. 18+.",
      },
      { property: "og:title", content: "Alle Casino Betaalmethoden 2026: 17 Opties Naast Elkaar" },
      {
        property: "og:description",
        content:
          "Ontdek de beste casino betaalmethoden van november 2026, getest op stortingssnelheid, uitbetalingen, limieten en kosten. 18+.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
        { name: "robots", content: "index, follow, max-image-preview:large" },
      ...ogImageMeta("betaalmethoden"),
      { property: "og:url", content: `${SITE.url}/betaalmethoden/` },
    ],
    links: [{ rel: "canonical", href: `${SITE.url}/betaalmethoden/` }],
  }),
  component: MethodsIndexPage,
});

function MethodsIndexPage() {
  const methods = getPublishedMethods();
  const categories = [...new Set(methods.map((m) => m.category))];

  return (
    <main className="mx-auto max-w-7xl px-4 py-12 sm:px-6">
      <h1 className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl">
        Beste casino betaalmethoden van november 2026
      </h1>
      <p className="mt-3 max-w-2xl text-muted-foreground">
        Elke methode is beoordeeld op stortsnelheid, uitbetaalmogelijkheden, limieten en kosten.
        Alleen methoden die daadwerkelijk bij Nederlandse aanbieders voorkomen, staan in dit overzicht.
      </p>

      <div className="mt-8 grid gap-6">
        <CoverageInfographic />
        <div className="grid gap-6 lg:grid-cols-2">
          <DepositSpeedInfographic />
          <PayoutSpeedInfographic />
        </div>
        <SpreadInfographic />
      </div>

      {categories.map((category) => (
        <section key={category} className="mt-10" aria-labelledby={`cat-${category}`}>
          <h2 id={`cat-${category}`} className="text-xl font-semibold text-foreground first-letter:uppercase">
            {category === "bank"
              ? "Casino betaalmethoden via je bank"
              : category === "wallet"
                ? "E-wallets"
                : category === "prepaid"
                  ? "Prepaid"
                  : category === "processor"
                    ? "Betaalverwerkers"
                    : "Overig"}
          </h2>
          <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {methods
              .filter((m) => m.category === category)
              .map((m) => (
                <Link key={m.id} to="/betaalmethoden/$slug/" params={{ slug: m.slug }} className="group">
                  <Card className="card-surface h-full transition-colors group-hover:border-primary/40">
                    <CardContent className="p-5">
                      <div className="flex items-center justify-between gap-2">
                        <MethodLogo slug={m.slug} name={m.name} />
                        <ArrowRight
                          className="h-4 w-4 text-muted-foreground transition-transform group-hover:translate-x-0.5"
                          aria-hidden
                        />
                      </div>
                      <h3 className="sr-only">{m.name}</h3>
                      <p className="mt-2 line-clamp-2 text-sm text-muted-foreground">{m.short_description}</p>
                      <div className="mt-4 flex flex-wrap gap-2">
                        <Badge variant="secondary">Storten vanaf {formatEuro(m.min_deposit_general)}</Badge>
                        {m.withdrawal_supported ? (
                          <Badge className="bg-success/15 text-success">Opnemen mogelijk</Badge>
                        ) : (
                          <Badge variant="outline">Alleen storten</Badge>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
          </div>
        </section>
      ))}

      <ProseSection id="kiezen" title="Welke betaalmethode past bij jouw manier van spelen?">
        <p>
          Bankgebonden methoden zijn in Nederland veruit het populairst. Met{" "}
          <Link to="/betaalmethoden/$slug/" params={{ slug: "ideal" }}>iDEAL</Link> betaal je direct
          vanuit je eigen bankapp en zie je de afschrijving meteen op je rekening staan, wat helpt
          om overzicht te houden. <Link to="/betaalmethoden/$slug/" params={{ slug: "sofort" }}>Sofort</Link>{" "}
          werkt vergelijkbaar en is vooral handig bij buitenlandse rekeningen, terwijl{" "}
          <Link to="/betaalmethoden/$slug/" params={{ slug: "trustly" }}>Trustly</Link> als enige
          bankoplossing in onze tests ook uitbetalingen naar dezelfde rekening ondersteunt.
        </p>
        <p>
          E-wallets zijn interessant als je snelheid bij het opnemen belangrijk vindt.{" "}
          <Link to="/betaalmethoden/$slug/" params={{ slug: "skrill" }}>Skrill</Link> en{" "}
          <Link to="/betaalmethoden/$slug/" params={{ slug: "neteller" }}>Neteller</Link> keerden in
          onze metingen het snelst uit, en{" "}
          <Link to="/betaalmethoden/$slug/" params={{ slug: "paypal" }}>PayPal</Link> combineert een
          vlotte kassa met een bekende koperbescherming. Let wel op wisselkoers- en
          opwaardeerkosten: die zijn per wallet verschillend.
        </p>
        <p>
          Wil je juist strak budgetteren, kies dan{" "}
          <Link to="/betaalmethoden/$slug/" params={{ slug: "paysafecard" }}>Paysafecard</Link>: je
          stort alleen wat er op de code staat. Op de telefoon zijn{" "}
          <Link to="/betaalmethoden/$slug/" params={{ slug: "apple-pay" }}>Apple Pay</Link> en{" "}
          <Link to="/betaalmethoden/$slug/" params={{ slug: "google-pay" }}>Google Pay</Link> de
          snelste opties. Weet je je keuze, gebruik dan de{" "}
          <Link to="/casinozoeker/">casinozoeker</Link> om alleen aanbieders te zien die jouw methode
          accepteren.
        </p>
      </ProseSection>

          <PageImageGallery path="/betaalmethoden" title="Betaalmethoden in beeld" />
    </main>
  );
}
